import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Routes, Route } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import {
  Compass,
  MapPin,
  Search,
  Star,
  X,
  Home,
  BookOpen,
  Hotel,
  LifeBuoy,
  Lock,
  Car,
  Clock,
  Calendar,
  ChevronRight,
  ShieldCheck,
  CreditCard,
} from "lucide-react";
import PopupAlert, { usePopup } from "./PopupAlert";

// Page Imports
import BookingPage from "./BookingPage";
import Explore from "./Explore";
import AdminPage from "./AdminPage";
import ContactPage from "./ContactPage";
import Stays from "./Stays";
import Journal from "./Journal";
import "./App.css";

const TOUR_PACKAGES = {
  tirupati: [
    {
      id: "t1",
      title: "Local Temples Package",
      duration: "06:00 AM - 02:00 PM",
      price: "Book Now",
      image:
        "https://tirupatitirumalainfo.com/wp-content/uploads/2016/12/Srinivasam-Complex.png.webp",
      plan: "Srinivasam Complex - Local Temples - Vishnu Nivasam",
      trips: [
        "Padmavathi Temple",
        "Vakulamatha Temple",
        "Kapileswara Swamy",
        "Govindaraja Swamy",
      ],
    },
    {
      id: "t2",
      title: "Surrounding Temples Tour",
      duration: "08:30 AM / 09:30 AM",
      price: "Book Now",
      image:
        "https://www.indiancarrental.com/uploads/carrent/79019_1697008790.jpg",
      plan: "Heritage Circuit - Luxury Coach with Guide",
      trips: [
        "Venugopala Swamy",
        "Vedanarayana Swamy",
        "Kalyana Venkateswara",
        "Pallikondeswara Swamy",
      ],
    },
    {
      id: "t3",
      title: "Srikalahasti & Kanipakam",
      duration: "On Demand Service",
      price: "Book Now",
      image:
        "https://www.taxiintirupati.com/images/destinations/kalahasti/kalahasti-kanipakam.jpg",
      plan: "Srikalahasti - Kanipakam - Tiruchanoor Tour",
      trips: ["Srikalahasti Darshan", "Kanipakam Temple", "Tiruchanoor Visit"],
    },
  ],
  south: [
    {
      id: "s1",
      title: "Kerala Backwaters Delight",
      duration: "5 Days / 4 Nights",
      price: "Book Now",
      image:
        "https://indiaholidaymall.com/images/blog/The-Magical-Backwaters-of-Kerala.jpg",
      plan: "Cochin - Munnar - Alleppey - Cochin",
      trips: [
        "Houseboat Stay",
        "Tea Plantation Visit",
        "Kathakali Performance",
      ],
    },
    {
      id: "s2",
      title: "Temple Trail of Tamil Nadu",
      duration: "6 Days / 5 Nights",
      price: "Book Now",
      image:
        "https://5.imimg.com/data5/SELLER/Default/2025/11/559078729/SI/AZ/VN/254347548/temple-heritage-trail-of-tamil-nadu-6-days.jpg",
      plan: "Chennai - Mahabalipuram - Pondicherry - Thanjavur - Madurai",
      trips: [
        "Shore Temple Visit",
        "French Colony Walk",
        "Meenakshi Temple Darshan",
      ],
    },
    {
      id: "s3",
      title: "Karnataka Heritage & Hills",
      duration: "7 Days / 6 Nights",
      price: "Book Now",
      image:
        "https://www.indianpanorama.in/assets/images/t-attraction/card/hasan.webp",
      plan: "Bangalore - Mysore - Coorg - Hampi",
      trips: ["Mysore Palace", "Abbey Falls", "Virupaksha Temple"],
    },
  ],
  north: [
    {
      id: "n1",
      title: "Golden Triangle Special",
      duration: "6 Days / 5 Nights",
      price: "Book Now",
      image:
        "https://majestictajtours.com/wp-content/uploads/2023/09/Golden-triangle-tour.jpg",
      plan: "Delhi - Agra - Jaipur - Delhi",
      trips: ["Taj Mahal Sunrise", "Amber Fort Ride", "Qutub Minar Visit"],
    },
    {
      id: "n2",
      title: "Royal Rajasthan Experience",
      duration: "8 Days / 7 Nights",
      price: "Book Now",
      image:
        "https://cdn.cholantours.com/packages/mehrangarh-fort-jodhpur-rajasthan.webp",
      plan: "Jaipur - Jodhpur - Jaisalmer - Udaipur",
      trips: ["Desert Camel Safari", "Mehrangarh Fort", "Lake Pichola Boating"],
    },
    {
      id: "n3",
      title: "Himalayan Gateway",
      duration: "7 Days / 6 Nights",
      price: "Book Now",
      image:
        "https://himalayangatewaytrek.com/wp-content/uploads/2026/01/S__11067395.jpg",
      plan: "Chandigarh - Shimla - Manali - Solang Valley",
      trips: ["Rohtang Pass", "Jakhoo Temple", "Mall Road Shopping"],
    },
  ],
};

const VEHICLE_DATA = [
  //   { name: "Hatchback", type: "Swift / Tiago", capacity: "4+1 Seats", pricePerKm: "₹12", dailyRate: "₹2,500" },
  //   { name: "Sedan", type: "Dzire / Etios", capacity: "4+1 Seats", pricePerKm: "₹14", dailyRate: "₹3,000" },
  //   { name: "SUV", type: "Innova / Ertiga", capacity: "6+1 Seats", pricePerKm: "₹18", dailyRate: "₹4,500" },
  //   { name: "Premium SUV", type: "Innova Crysta", capacity: "7+1 Seats", pricePerKm: "₹22", dailyRate: "₹5,500" },
  //   { name: "Tempo Traveller", type: "Force Traveler", capacity: "12+1 Seats", pricePerKm: "₹26", dailyRate: "₹7,000" },
  //   { name: "Mini Bus", type: "Urbania", capacity: "17+1 Seats", pricePerKm: "₹35", dailyRate: "₹10,000" }
];

const TravelApp = () => {
  const navigate = useNavigate();
  const scrollRef = useRef(null);
  const [showWelcomePopup, setShowWelcomePopup] = useState(false);
  const { popup, showPopup, closePopup } = usePopup();

  // 1. Update your URL constants to be relative
  const API_URL_REVIEWS = "/api/reviews";
  const API_URL_DESTINATIONS = "/api/destinations";

  // --- States ---
  const [search, setSearch] = useState("");
  const [isAdminAuthOpen, setIsAdminAuthOpen] = useState(false);
  const [adminCreds, setAdminCreds] = useState({ user: "", pass: "" });
  const [authError, setAuthError] = useState(false);
  const [destinations, setDestinations] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [userRating, setUserRating] = useState(5);
  const [reviewText, setReviewText] = useState("");
  const [reviewerName, setReviewerName] = useState("");
  const [selectedImage, setSelectedImage] = useState(null);
  const [activePackageTab, setActivePackageTab] = useState("tirupati");
  const [fleetItems, setFleetItems] = useState([]);
  const [onDemandToursList, setOnDemandToursList] = useState([]);

  // Customer Photos States
  const [customerPhotos, setCustomerPhotos] = useState([]);
  const [isPhotoModalOpen, setIsPhotoModalOpen] = useState(false);
  const [tourPhotoData, setTourPhotoData] = useState({
    name: "",
    location: "",
    imgUrl: "",
  });
  const API_URL_PHOTOS = "/api/customer-photos";

  // Promotions/Offers State
  const [activeOffer, setActiveOffer] = useState(null);
  const [showOfferPopup, setShowOfferPopup] = useState(false);
  // REPLACE your current "const filtered = ..." with this:
  const filtered = Array.isArray(destinations)
    ? destinations.filter((d) =>
        d?.name?.toLowerCase().includes(search.toLowerCase()),
      )
    : [];

  const handleImageChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const reader = new FileReader();
      reader.onloadend = () => {
        setSelectedImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearch(value);
    if (value.toLowerCase() === "admin") {
      setIsAdminAuthOpen(true);
      setSearch(""); // Clear search so modal doesn't re-trigger immediately
    }
  };

  const handleAdminAuthSubmit = (e) => {
    e.preventDefault();
    if (adminCreds.user === "admin" && adminCreds.pass === "admin123") {
      sessionStorage.setItem("isAdminAuthenticated", "true");
      navigate("/admin");
      setIsAdminAuthOpen(false);
      setAdminCreds({ user: "", pass: "" });
      setAuthError(false);
    } else {
      setAuthError(true);
      setTimeout(() => setAuthError(false), 2000);
    }
  };

  const handleSearchSubmit = (e) => {
    if (e) e.preventDefault();
    if (search.trim()) {
      const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(search)}`;
      window.open(mapsUrl, "_blank");
    }
  };

  const handleRedirect = () => navigate("/contact");

  // --- API Effects ---

  // 2. Update your fetchDestinations effect
  useEffect(() => {
    const fetchDestinations = async () => {
      try {
        const response = await fetch(API_URL_DESTINATIONS);
        const data = await response.json();

        // CRITICAL: Only set state if the data is actually an array
        if (Array.isArray(data)) {
          setDestinations(data);
        } else {
          console.error("API did not return an array:", data);
          setDestinations([]); // Fallback
        }
      } catch (error) {
        console.error("Fetch error:", error);
        setDestinations([]); // Fallback on network error
      } finally {
        setLoading(false);
      }
    };
    fetchDestinations();
  }, [API_URL_DESTINATIONS]);

  // --- Popup Logic ---
  const [popupFormData, setPopupFormData] = useState({
    name: "",
    email: "",
    number: "",
    location: "",
  });

  const handlePopupChange = (e) => {
    const { name, value } = e.target;
    setPopupFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePopupSubmit = async (e) => {
    e.preventDefault();
    try {
      // Change 'http://localhost:5000/api/bookings' to '/api.php?request=bookings'
      const response = await fetch("/api/bookings", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(popupFormData),
      });

      if (response.ok) {
        setShowWelcomePopup(false);
        setPopupFormData({ name: "", email: "", number: "", location: "" });
        showPopup(
          `✨ Success! Trip booked for ${popupFormData.name}.`,
          "success",
        );
      } else {
        const errorData = await response.json();
        showPopup(
          `Booking Failed: ${errorData.message || "Unknown error"}`,
          "error",
        );
      }
    } catch (error) {
      console.error("Popup Submit Error:", error);
      showPopup(
        "Server is waking up. Please try again in 30 seconds.",
        "error",
      );
    }
  };

  useEffect(() => {
    const hasSeenPopup = sessionStorage.getItem("hasSeenWelcome");
    if (!hasSeenPopup) {
      const timer = setTimeout(() => {
        setShowWelcomePopup(true);
        sessionStorage.setItem("hasSeenWelcome", "true");
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  // --- Fetch Reviews ---
  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const res = await axios.get(API_URL_REVIEWS);
        setTestimonials(res.data);
      } catch (err) {
        console.error("Error fetching reviews:", err);
      }
    };
    fetchReviews();
  }, [API_URL_REVIEWS]);

  const totalReviews = testimonials.length;
  const averageRating =
    totalReviews > 0
      ? (
          testimonials.reduce((acc, t) => acc + t.stars, 0) / totalReviews
        ).toFixed(1)
      : 0;

  // Fetch Fleet and On-Demand Tours and Customer Photos
  useEffect(() => {
    const fetchDynamicData = async () => {
      try {
        const fleetRes = await axios.get("/api/fleet");
        setFleetItems(fleetRes.data);
        const toursRes = await axios.get("/api/ondemand-tours");
        setOnDemandToursList(toursRes.data);
        const photosRes = await axios.get(API_URL_PHOTOS);
        setCustomerPhotos(photosRes.data);

        // Fetch Promotional Offers
        const offersRes = await axios.get("/api/offers");
        if (offersRes.data && offersRes.data.length > 0) {
          setActiveOffer(offersRes.data[0]);
        }
      } catch (err) {
        console.error("Error fetching dynamic data:", err);
      }
    };
    fetchDynamicData();
  }, []);

  // Auto-Scroll Effect
  useEffect(() => {
    const scrollContainer = scrollRef.current;
    if (scrollContainer && destinations.length > 0) {
      let scrollAmount = 0;
      const step = 1;
      const scrollStep = () => {
        scrollAmount += step;
        if (scrollAmount >= scrollContainer.scrollWidth / 2) scrollAmount = 0;
        scrollContainer.scrollLeft = scrollAmount;
        requestAnimationFrame(scrollStep);
      };
      const animationId = requestAnimationFrame(scrollStep);
      return () => cancelAnimationFrame(animationId);
    }
  }, [destinations]);

  // Recurring Offer Popup Effect (Every 5 minutes)
  useEffect(() => {
    if (activeOffer) {
      // Show shortly after load
      const initialTimeout = setTimeout(() => {
        setShowOfferPopup(true);
      }, 1500);

      // Repeat every 5 minutes
      const intervalId = setInterval(
        () => {
          setShowOfferPopup(true);
        },
        5 * 60 * 1000,
      );

      return () => {
        clearTimeout(initialTimeout);
        clearInterval(intervalId);
      };
    }
  }, [activeOffer]);

  // --- Submit Review Logic ---
  const submitRating = async () => {
    if (!reviewerName.trim() || !reviewText.trim()) {
      showPopup("Please enter both your name and a review message.", "error");
      return;
    }

    const newEntry = {
      name: reviewerName.trim(),
      stars: userRating,
      text: reviewText.trim(),
      avatar:
        selectedImage ||
        `https://i.pravatar.cc/150?u=${encodeURIComponent(reviewerName.trim() + Math.random())}`,
    };

    try {
      const res = await axios.post(API_URL_REVIEWS, newEntry);
      setTestimonials((prev) => [res.data, ...prev]);

      setIsModalOpen(false);
      setReviewerName("");
      setReviewText("");
      setUserRating(5);
      setSelectedImage(null);

      showPopup("🌟 Thank you for your review!", "success");
    } catch (err) {
      console.error("Error saving review:", err);
      showPopup("Failed to save review. Please try again.", "error");
    }
  };

  const submitTourPhoto = async () => {
    if (
      !tourPhotoData.name.trim() ||
      !tourPhotoData.location.trim() ||
      !tourPhotoData.imgUrl
    ) {
      showPopup("Please fill all fields and select a photo.", "error");
      return;
    }
    try {
      const res = await axios.post(API_URL_PHOTOS, {
        customerName: tourPhotoData.name.trim(),
        location: tourPhotoData.location.trim(),
        imgUrl: tourPhotoData.imgUrl,
      });
      setCustomerPhotos([res.data.data, ...customerPhotos]);
      setIsPhotoModalOpen(false);
      setTourPhotoData({ name: "", location: "", imgUrl: "" });
      showPopup("Tour photo uploaded successfully!", "success");
    } catch (err) {
      console.error("Error saving photo:", err);
      showPopup("Failed to upload photo. Please try again.", "error");
    }
  };

  return (
    <div className="app-container">
      {/* Global Popup Alert */}
      <PopupAlert popup={popup} onClose={closePopup} />
      {/* Navbar */}
      <nav className="navbar">
        <div className="logo">
          <img
            src={require("./giuide-Logo.jpg")}
            alt="Sri Balaji Guide Logo"
            className="app-custom-logo"
          />
          <span>Sri Balaji Guide</span>
        </div>
        <div className="nav-links">
          <button
            className="nav-explore-btn"
            onClick={() => navigate("/explore")}
            style={{
              background: "none",
              border: "none",
              color: "#94a3b8",
              textTransform: "uppercase",
              fontSize: "0.75rem",
              letterSpacing: "2px",
              cursor: "pointer",
              transition: "color 0.3s",
              padding: 0,
            }}
          >
            Explore
          </button>
          <button
            className="nav-explore-btn"
            onClick={() => navigate("/stays")}
            style={{
              background: "none",
              border: "none",
              color: "#94a3b8",
              textTransform: "uppercase",
              fontSize: "0.75rem",
              letterSpacing: "2px",
              cursor: "pointer",
              transition: "color 0.3s",
              padding: 0,
              margin: "0 1rem",
            }}
          >
            Stays
          </button>
          <button
            className="nav-explore-btn"
            onClick={() => navigate("/journal")}
            style={{
              background: "none",
              border: "none",
              color: "#94a3b8",
              textTransform: "uppercase",
              fontSize: "0.75rem",
              letterSpacing: "2px",
              cursor: "pointer",
              transition: "color 0.3s",
              padding: 0,
              margin: "0 1rem",
            }}
          >
            Journal
          </button>
          <a href="#support">Support</a>
        </div>
        <button
          className="btn-primary nav-book-btn"
          onClick={() => navigate("/bookpage")}
        >
          Book Trip
        </button>
      </nav>

      {/* Mobile Bottom Navigation Bar (Zepto/Swiggy Style) */}
      <nav className="mobile-bottom-nav">
        <div className="mobile-nav-item" onClick={() => navigate("/")}>
          <Home size={20} className="mobile-nav-icon" />
          <span>Home</span>
        </div>
        <div className="mobile-nav-item" onClick={() => navigate("/explore")}>
          <Compass size={20} className="mobile-nav-icon" />
          <span>Explore</span>
        </div>
        <div
          className="mobile-nav-item"
          onClick={() => navigate("/stays")}
          style={{ cursor: "pointer" }}
        >
          <Hotel size={20} className="mobile-nav-icon" />
          <span>Stays</span>
        </div>
        <div
          className="mobile-nav-item"
          onClick={() => navigate("/journal")}
          style={{ cursor: "pointer" }}
        >
          <BookOpen size={20} className="mobile-nav-icon" />
          <span>Journal</span>
        </div>
        <a href="#support" className="mobile-nav-item">
          <LifeBuoy size={20} className="mobile-nav-icon" />
          <span>Support</span>
        </a>
      </nav>

      {/* Hero */}
      <section className="hero">
        <img
          src="https://images.unsplash.com/photo-1501785888041-af3ef285b470?auto=format&fit=crop&w=1920&q=80"
          className="hero-bg"
          alt="Hero"
        />
        <div className="hero-overlay"></div>
        <div className="hero-content">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: [0, -10, 0] }}
            transition={{
              opacity: { duration: 1 },
              y: { repeat: Infinity, duration: 2, ease: "easeInOut" },
            }}
            className="hero-title"
          >
            Beyond the <br />
            <span>Horizon.</span>
          </motion.h1>
          <p className="hero-subtitle">
            Travel for those who have seen it all.
          </p>
          <div className="search-bar">
            <Search className="search-icon" />
            <input
              type="text"
              placeholder="Where to next?"
              value={search}
              onChange={handleSearchChange}
              onKeyPress={(e) => e.key === "Enter" && handleSearchSubmit()}
            />
            <button className="search-btn" onClick={handleSearchSubmit}>
              <Compass size={20} />
            </button>
          </div>
        </div>
      </section>

      {/* Welcome Popup */}
      <AnimatePresence>
        {showWelcomePopup && (
          <motion.div
            className="modal-overlay-fixed"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="offer-popup popup-floating welcome-popup"
              initial={{ scale: 0.8, y: 50, rotateX: 20 }}
              animate={{ scale: 1, y: 0, rotateX: 0 }}
              exit={{ scale: 0.8, opacity: 0, y: 20 }}
              transition={{ type: "spring", damping: 15 }}
            >
              <button
                className="close-popup-top"
                onClick={() => setShowWelcomePopup(false)}
              >
                <X size={24} />
              </button>

              <div className="offer-header">
                <span
                  className="offer-badge"
                  style={{
                    background: "rgba(56, 189, 248, 0.1)",
                    color: "#38bdf8",
                  }}
                >
                  Welcome to Our Family
                </span>
                <h2 style={{ fontSize: "1.8rem" }}>Thank You for Choosing</h2>
                <h3
                  style={{
                    color: "#38bdf8",
                    marginTop: "5px",
                    fontSize: "1.5rem",
                  }}
                >
                  Sri Balaji Guide
                </h3>
              </div>

              <div className="offer-body" style={{ textAlign: "center" }}>
                <p
                  style={{
                    color: "#94a3b8",
                    fontSize: "0.95rem",
                    lineHeight: "1.6",
                  }}
                >
                  We are honored to be part of your journey. Our expert guides
                  are dedicated to making every moment of your pilgrimage or
                  luxury trip extraordinary.
                </p>
                <div
                  className="discount-circle"
                  style={{
                    background: "linear-gradient(135deg, #38bdf8, #818cf8)",
                    width: "80px",
                    height: "80px",
                    margin: "20px auto",
                  }}
                >
                  <span style={{ fontSize: "0.9rem", color: "white" }}>
                    Premium Service
                  </span>
                </div>
              </div>

              <div className="offer-footer">
                <p
                  style={{
                    fontSize: "0.85rem",
                    color: "#64748b",
                    marginBottom: "15px",
                  }}
                >
                  Start your spiritual exploration today:
                </p>
                <button
                  className="btn-primary"
                  style={{ width: "100%", padding: "12px" }}
                  onClick={() => {
                    setShowWelcomePopup(false);
                    navigate("/explore");
                  }}
                >
                  Explore Destinations
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Promotional Offer Popup */}
      <AnimatePresence>
        {showOfferPopup && activeOffer && (
          <motion.div
            className="modal-overlay-fixed"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="offer-popup popup-floating"
              initial={{ scale: 0.8, y: 50, rotateX: 20 }}
              animate={{ scale: 1, y: 0, rotateX: 0 }}
              exit={{ scale: 0.8, opacity: 0, y: 20 }}
              transition={{ type: "spring", damping: 15 }}
            >
              <button
                className="close-popup-top"
                onClick={() => setShowOfferPopup(false)}
              >
                <X size={24} />
              </button>

              <div className="offer-header">
                <span className="offer-badge">Limited Time Offer</span>
                <h2>{activeOffer.title}</h2>
              </div>

              <div className="offer-body">
                <p>{activeOffer.description}</p>
                <div className="discount-circle">
                  <span>{activeOffer.discountText}</span>
                </div>
              </div>

              <div className="offer-footer">
                <p>Use code at checkout:</p>
                <div className="coupon-box">
                  <strong className="coupon-code">
                    {activeOffer.couponCode}
                  </strong>
                  <button
                    className="copy-btn"
                    onClick={() => {
                      navigator.clipboard.writeText(activeOffer.couponCode);
                      showPopup("Coupon copied to clipboard!", "success");
                      setShowOfferPopup(false);
                    }}
                  >
                    Copy & Apply
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Destinations */}
      <section id="stays" className="destinations">
        <h2 className="section-title">Trending Destinations</h2>
        <div className="scroll-wrapper">
          <div className="grid-layout" ref={scrollRef}>
            {loading ? (
              <p>Loading adventures...</p>
            ) : (
              <AnimatePresence>
                {filtered.map((item) => (
                  <motion.div key={item._id} layout className="card">
                    <img src={item.img} alt={item.name} />
                    <div className="card-overlay">
                      <span className="location">
                        <MapPin size={14} /> {item.country || item.state}
                      </span>
                      <h3>{item.name}</h3>
                      {/* PRESERVED: Centered Rating Display */}
                      <div
                        className="rating"
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          width: "100%",
                        }}
                      >
                        <Star size={14} className="star" /> {item.rating}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            )}
          </div>
        </div>
      </section>

      {/* Tour Packages Section */}
      <section id="packages" className="packages-section">
        <h2 className="section-title">Luxury Tour Packages</h2>

        <div className="tabs-container">
          <button
            className={`tab-btn ${activePackageTab === "tirupati" ? "active" : ""}`}
            onClick={() => setActivePackageTab("tirupati")}
          >
            Tirupati Special
          </button>
          <button
            className={`tab-btn ${activePackageTab === "south" ? "active" : ""}`}
            onClick={() => setActivePackageTab("south")}
          >
            South India
          </button>
          <button
            className={`tab-btn ${activePackageTab === "north" ? "active" : ""}`}
            onClick={() => setActivePackageTab("north")}
          >
            North India
          </button>
        </div>

        <div className="packages-grid">
          {TOUR_PACKAGES[activePackageTab].map((pkg) => (
            <motion.div
              key={pkg.id}
              className="package-card"
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <div className="package-image-wrapper">
                <img src={pkg.image} alt={pkg.title} className="package-img" />
                <div className="package-price-badge">{pkg.price}</div>
              </div>
              <div className="package-info">
                <div className="package-tag">
                  <Calendar size={14} /> {pkg.duration}
                </div>
                <h3>{pkg.title}</h3>
                <p className="package-plan">
                  <strong>Plan:</strong> {pkg.plan}
                </p>
                <div className="package-trips">
                  {pkg.trips.map((trip, idx) => (
                    <span key={idx} className="trip-tag">
                      <ShieldCheck size={12} /> {trip}
                    </span>
                  ))}
                </div>
                <button
                  className="book-btn-small"
                  onClick={() => navigate("/bookpage")}
                >
                  Enquire Now
                </button>
              </div>
            </motion.div>
          ))}
        </div>

        {activePackageTab === "tirupati" && (
          <div className="on-demand-section">
            <h3 className="sub-section-title">
              Other Tirupati Tours (On Demand)
            </h3>
            <div className="on-demand-grid">
              {onDemandToursList.length > 0 ? (
                onDemandToursList.map((tour) => (
                  <div className="on-demand-card" key={tour._id}>
                    <h4>{tour.title}</h4>
                    <p>{tour.price}</p>
                  </div>
                ))
              ) : (
                <>
                  <div className="on-demand-card">
                    <h4>Sri Kalahasthi & Local</h4>
                    <p>₹400</p>
                  </div>
                  <div className="on-demand-card">
                    <h4>Kanipakam & Local</h4>
                    <p>₹500</p>
                  </div>
                  <div className="on-demand-card">
                    <h4>Talakona & Local</h4>
                    <p>₹400</p>
                  </div>
                  <div className="on-demand-card">
                    <h4>Vellore Golden Temple</h4>
                    <p>₹900</p>
                  </div>
                  <div className="on-demand-card">
                    <h4>Kanipakam - Tiruchanoor - Srikalahasti</h4>
                    <p>₹900</p>
                  </div>
                </>
              )}
            </div>
            <div className="tirupati-contact-info">
              <p>
                For Further Details Please Contact: <strong>9381849299</strong>,{" "}
                <strong>9494028804</strong>
              </p>
              <p style={{ marginTop: "10px", fontSize: "0.8rem" }}>
                * Tours operated with Non-A/C Luxury Coach with Guide Facility
              </p>
            </div>
          </div>
        )}

        {/* Vehicle pricing sub-section */}
        <div className="vehicle-pricing">
          <div className="vehicle-header">
            <h3>
              <Car size={24} /> Premium Fleet & Pricing
            </h3>
            <p>
              Choose from our wide range of well-maintained vehicles for a
              comfortable journey.
            </p>
          </div>

          <div className="vehicle-scroll-container">
            <table className="vehicle-table">
              <thead>
                <tr>
                  <th>Vehicle Type</th>
                  <th>Models</th>
                  <th>Capacity</th>
                  <th>Price / KM</th>
                  <th>Daily Rate</th>
                </tr>
              </thead>
              <tbody>
                {(fleetItems.length > 0 ? fleetItems : VEHICLE_DATA).map(
                  (v, idx) => (
                    <tr key={idx}>
                      <td className="font-bold">{v.vehicleType || v.name}</td>
                      <td>{v.models || v.type}</td>
                      <td>{v.capacity}</td>
                      <td className="accent-text">{v.pricePerKm}</td>
                      <td className="accent-text">{v.dailyRate}</td>
                    </tr>
                  ),
                )}
              </tbody>
            </table>
          </div>

          <div className="travel-details-grid">
            <div className="detail-item-box">
              <Clock size={20} className="detail-icon" />
              <div>
                <h4>Operating Hours</h4>
                <p>24/7 Professional Service Available</p>
              </div>
            </div>
            <div className="detail-item-box">
              <MapPin size={20} className="detail-icon" />
              <div>
                <h4>Pickup & Drop</h4>
                <p>Airport, Railway Station & Hotels</p>
              </div>
            </div>
            <div className="detail-item-box">
              <CreditCard size={20} className="detail-icon" />
              <div>
                <h4>Easy Payment</h4>
                <p>Cash, UPI & Bank Transfer Accepted</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Guide Info */}
      <section id="support" className="newsletter">
        <div className="newsletter-content">
          <span className="badge">Verified Experts</span>
          <h2>Your Adventure Guides</h2>
          <div className="guide-cards-wrapper">
            <div className="guide-card">
              <div className="guide-info">
                <div className="detail-item">
                  <strong>Primary Contact</strong>
                  <p>SRI BALAJI GUIDE</p>
                </div>
                <div className="detail-item">
                  <strong>Phone</strong>
                  <a href="tel:94944028804" className="phone-link">
                    94944028804
                  </a>
                </div>
              </div>
              <button
                className="contact-btn"
                onClick={() => (window.location.href = "tel:94944028804")}
              >
                <span>Call Office</span>
              </button>
            </div>

            <div className="guide-card">
              <div className="guide-info">
                <div className="detail-item">
                  <strong>Mobile Support</strong>
                  <p>24/7 RESERVATION</p>
                </div>
                <div className="detail-item">
                  <strong>Phone</strong>
                  <a href="tel:+91-9494028804" className="phone-link">
                    +91-9494028804
                  </a>
                </div>
              </div>
              <button
                className="contact-btn"
                onClick={() => (window.location.href = "tel:+91-9494028804")}
              >
                <span>Connect Now</span>
              </button>
            </div>

            <div className="guide-card">
              <div className="guide-info">
                <div className="detail-item">
                  <strong>Direct Line</strong>
                  <p>KALYAN</p>
                </div>
                <div className="detail-item">
                  <strong>Phone</strong>
                  <a href="tel:+91-9381849299" className="phone-link">
                    +91 93818 49299
                  </a>
                </div>
              </div>
              <button
                className="contact-btn"
                onClick={() => (window.location.href = "tel:+919381849299")}
              >
                <span>Direct Call</span>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Traveler Memories (Customer Photos) */}
      <section id="memories" className="memories-section">
        <div className="section-header-centered">
          <span className="badge">Community</span>
          <h2
            className="title"
            style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: "clamp(2rem, 5vw, 3.5rem)",
            }}
          >
            Traveler Memories
          </h2>
          <p style={{ color: "#94a3b8", marginBottom: "2rem" }}>
            Share your favorite moments from your journey with us.
          </p>
          <button
            className="btn-primary"
            onClick={() => setIsPhotoModalOpen(true)}
          >
            Upload Your Photo
          </button>
        </div>

        <div className="memories-grid">
          {customerPhotos.length > 0 ? (
            customerPhotos.map((photo) => (
              <motion.div
                className="memory-card"
                key={photo._id}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
              >
                <img src={photo.imgUrl} alt={photo.location} loading="lazy" />
                <div className="memory-info">
                  <span className="memory-location">
                    <MapPin size={14} /> {photo.location}
                  </span>
                  <span className="memory-author">by {photo.customerName}</span>
                </div>
              </motion.div>
            ))
          ) : (
            <p
              style={{
                textAlign: "center",
                color: "#94a3b8",
                width: "100%",
                gridColumn: "1 / -1",
              }}
            >
              No photos uploaded yet. Be the first to share your journey!
            </p>
          )}
        </div>
      </section>

      {/* Testimonials */}
      <section id="journal" className="testimonial-section">
        <div className="testimonial-layout centered">
          <div className="rating-summary">
            <h2 className="title">What People Say</h2>
            <div className="overall-stats">
              <div className="stats-inner">
                <span className="average-num">{averageRating}</span>
                <div className="average-stars">
                  {[1, 2, 3, 4, 5].map((s) => (
                    <span
                      key={s}
                      className={
                        s <= Math.round(averageRating)
                          ? "star-filled"
                          : "star-empty"
                      }
                    >
                      ★
                    </span>
                  ))}
                </div>
                <p className="total-count">Trusted by {totalReviews} members</p>
              </div>
            </div>
            <button
              className="btn-primary"
              onClick={() => setIsModalOpen(true)}
            >
              Leave a Review
            </button>
          </div>

          <div className="testimonial-container full-width">
            <div className="scroll-wrapper">
              <div className="scroll-track">
                {testimonials.map((t) => (
                  <div className="testimonial-card" key={t._id}>
                    <div className="stars">
                      {"★".repeat(t.stars)}
                      {"☆".repeat(5 - t.stars)}
                    </div>
                    <p className="content">{t.text}</p>
                    <div className="user-info">
                      <img src={t.avatar} alt={t.name} className="avatar" />
                      <div className="user-details">
                        <h4>{t.name}</h4>
                        <span>{t.role || "Explorer"}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Photo Upload Modal */}
      <AnimatePresence>
        {isPhotoModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="modal-overlay"
            onClick={() => setIsPhotoModalOpen(false)}
          >
            <motion.div
              className="modal-content"
              onClick={(e) => e.stopPropagation()}
            >
              <h3>Share Your Tour Photo</h3>
              <div className="photo-upload-container">
                <label htmlFor="tour-photo-upload" className="photo-label">
                  {tourPhotoData.imgUrl ? (
                    <img
                      src={tourPhotoData.imgUrl}
                      alt="Preview"
                      className="upload-preview"
                    />
                  ) : (
                    <div className="upload-placeholder">
                      <span>+</span>
                      <p>Select Image</p>
                    </div>
                  )}
                </label>
                <input
                  id="tour-photo-upload"
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    if (e.target.files && e.target.files[0]) {
                      const reader = new FileReader();
                      reader.onloadend = () =>
                        setTourPhotoData({
                          ...tourPhotoData,
                          imgUrl: reader.result,
                        });
                      reader.readAsDataURL(e.target.files[0]);
                    }
                  }}
                  hidden
                />
              </div>
              <input
                type="text"
                placeholder="Your Name"
                className="modal-input"
                value={tourPhotoData.name}
                onChange={(e) =>
                  setTourPhotoData({ ...tourPhotoData, name: e.target.value })
                }
              />
              <input
                type="text"
                placeholder="Location visited (e.g. Taj Mahal)"
                className="modal-input"
                value={tourPhotoData.location}
                onChange={(e) =>
                  setTourPhotoData({
                    ...tourPhotoData,
                    location: e.target.value,
                  })
                }
              />
              <button className="btn-primary" onClick={submitTourPhoto}>
                Upload to Gallery
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Rating Modal */}
      <AnimatePresence>
        {isModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="modal-overlay"
            onClick={() => setIsModalOpen(false)}
          >
            <motion.div
              className="modal-content"
              onClick={(e) => e.stopPropagation()}
            >
              <h3>Share Your Experience</h3>
              <div className="photo-upload-container">
                <label htmlFor="photo-upload" className="photo-label">
                  {selectedImage ? (
                    <img
                      src={selectedImage}
                      alt="Preview"
                      className="upload-preview"
                    />
                  ) : (
                    <div className="upload-placeholder">
                      <span>+</span>
                      <p>Add Photo</p>
                    </div>
                  )}
                </label>
                <input
                  id="photo-upload"
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  hidden
                />
              </div>
              <input
                type="text"
                placeholder="Your Name"
                className="modal-input"
                value={reviewerName}
                onChange={(e) => setReviewerName(e.target.value)}
              />
              <div className="star-input">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star
                    key={star}
                    size={28}
                    style={{ cursor: "pointer" }}
                    fill={star <= userRating ? "#38bdf8" : "transparent"}
                    color={star <= userRating ? "#38bdf8" : "#94a3b8"}
                    onClick={() => setUserRating(star)}
                  />
                ))}
              </div>
              <textarea
                placeholder="Describe your journey..."
                value={reviewText}
                onChange={(e) => setReviewText(e.target.value)}
              />
              <button className="btn-primary" onClick={submitRating}>
                Send Review
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-content">
          <div className="footer-brand">
            <div className="logo">
              <img
                src={require("./giuide-Logo.jpg")}
                alt="Sri Balaji Guide Logo"
                className="app-custom-logo"
              />
              <span>Sri Balaji Guide Services</span>
            </div>
            <p>
              Curating extraordinary experiences for the modern explorer. Beyond
              the map, lies the soul of travel.
            </p>
          </div>
          <div className="footer-links">
            <h4>Navigation</h4>
            <ul>
              <li>
                <span
                  className="footer-nav-link"
                  onClick={() => navigate("/explore")}
                >
                  Explore
                </span>
              </li>
              <li>
                <span
                  className="footer-nav-link"
                  onClick={() => navigate("/stays")}
                >
                  Luxury Stays
                </span>
              </li>
              <li>
                <span
                  className="footer-nav-link"
                  onClick={() => navigate("/journal")}
                >
                  Travel Journal
                </span>
              </li>
            </ul>
          </div>
          <div className="footer-links">
            <h4>Support</h4>
            <ul>
              <li>
                <a href="#help">Help Center</a>
              </li>

              <li>
                <a href="#privacy">Privacy Policy</a>
              </li>

              <li>
                <a href="#terms">Terms of Use</a>
              </li>

              <li>
                {/* Clean Button Implementation */}
                <button onClick={handleRedirect} className="contact-btn-link">
                  Contact Us
                </button>
              </li>
            </ul>
          </div>
          <div className="footer-social">
            <h4>Connect</h4>
            <div className="social-icons">
              <span
                onClick={() => window.open("https://instagram.com", "_blank")}
                title="Instagram"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  fill="currentColor"
                  className="bi bi-instagram"
                  viewBox="0 0 16 16"
                >
                  <path d="M8 0C5.829 0 5.556.01 4.703.048 3.85.088 3.269.222 2.76.42a3.9 3.9 0 0 0-1.417.923A3.9 3.9 0 0 0 .42 2.76C.222 3.268.087 3.85.048 4.7.01 5.555 0 5.827 0 8.001c0 2.172.01 2.444.048 3.297.04.852.174 1.433.372 1.942.205.526.478.972.923 1.417.444.445.89.719 1.416.923.51.198 1.09.333 1.942.372C5.555 15.99 5.827 16 8 16s2.444-.01 3.298-.048c.851-.04 1.434-.174 1.943-.372a3.9 3.9 0 0 0 1.416-.923c.445-.445.718-.891.923-1.417.197-.509.332-1.09.372-1.942C15.99 10.445 16 10.173 16 8s-.01-2.445-.048-3.299c-.04-.851-.175-1.433-.372-1.941a3.9 3.9 0 0 0-.923-1.417A3.9 3.9 0 0 0 13.24.42c-.51-.198-1.092-.333-1.943-.372C10.443.01 10.172 0 7.998 0zm-.717 1.442h.718c2.136 0 2.389.007 3.232.046.78.035 1.204.166 1.486.275.373.145.64.319.92.599s.453.546.598.92c.11.281.24.705.275 1.485.039.843.047 1.096.047 3.231s-.008 2.389-.047 3.232c-.035.78-.166 1.203-.275 1.485a2.5 2.5 0 0 1-.599.919c-.28.28-.546.453-.92.598-.28.11-.704.24-1.485.276-.843.038-1.096.047-3.232.047s-2.39-.009-3.233-.047c-.78-.036-1.203-.166-1.485-.276a2.5 2.5 0 0 1-.92-.598 2.5 2.5 0 0 1-.6-.92c-.109-.281-.24-.705-.275-1.485-.038-.843-.046-1.096-.046-3.233s.008-2.388.046-3.231c.036-.78.166-1.204.276-1.486.145-.373.319-.64.599-.92s.546-.453.92-.598c.282-.11.705-.24 1.485-.276.738-.034 1.024-.044 2.515-.045zm4.988 1.328a.96.96 0 1 0 0 1.92.96.96 0 0 0 0-1.92m-4.27 1.122a4.109 4.109 0 1 0 0 8.217 4.109 4.109 0 0 0 0-8.217m0 1.441a2.667 2.667 0 1 1 0 5.334 2.667 2.667 0 0 1 0-5.334" />
                </svg>
              </span>
              <span
                onClick={() => window.open("https://twitter.com", "_blank")}
                title="Twitter"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  fill="currentColor"
                  className="bi bi-twitter-x"
                  viewBox="0 0 16 16"
                >
                  <path d="M12.6.75h2.454l-5.36 6.142L16 15.25h-4.937l-3.867-5.07-4.425 5.07H.316l5.733-6.57L0 .75h5.063l3.495 4.633L12.601.75Zm-.86 13.028h1.36L4.323 2.145H2.865l8.875 11.633Z" />
                </svg>
              </span>
              <span
                onClick={() => window.open("https://facebook.com", "_blank")}
                title="Facebook"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  fill="currentColor"
                  className="bi bi-facebook"
                  viewBox="0 0 16 16"
                >
                  <path d="M16 8.049c0-4.446-3.582-8.05-8-8.05C3.58 0-.002 3.603-.002 8.05c0 4.017 2.926 7.347 6.75 7.951v-5.625h-2.03V8.05H6.75V6.275c0-2.017 1.195-3.131 3.022-3.131.876 0 1.791.157 1.791.157v1.98h-1.009c-.993 0-1.303.621-1.303 1.258v1.51h2.218l-.354 2.326H9.25V16c3.824-.604 6.75-3.934 6.75-7.951z" />
                </svg>
              </span>
            </div>
          </div>
        </div>
        <hr className="footer-divider" />
        <div className="footer-bottom">
          <p>
            &copy; {new Date().getFullYear()}-SRI BALAJI GUIDE SERIVCE. Crafted
            for the curious.
          </p>
        </div>
      </footer>

      {/* Admin Authentication Modal */}
      <AnimatePresence>
        {isAdminAuthOpen && (
          <motion.div
            className="modal-overlay-fixed"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsAdminAuthOpen(false)}
          >
            <motion.div
              className={`auth-card ${authError ? "shake" : ""}`}
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="auth-header">
                <div className="auth-icon-circle">
                  <Lock size={24} />
                </div>
                <h3>Admin Access</h3>
                <p>Please enter credentials to continue</p>
              </div>

              <form onSubmit={handleAdminAuthSubmit} className="auth-form">
                <div className="auth-input-group">
                  <input
                    type="text"
                    placeholder="Username"
                    value={adminCreds.user}
                    onChange={(e) =>
                      setAdminCreds({ ...adminCreds, user: e.target.value })
                    }
                    required
                  />
                </div>
                <div className="auth-input-group">
                  <input
                    type="password"
                    placeholder="Password"
                    value={adminCreds.pass}
                    onChange={(e) =>
                      setAdminCreds({ ...adminCreds, pass: e.target.value })
                    }
                    required
                  />
                </div>
                {authError && (
                  <p className="error-text">Invalid credentials. Try again.</p>
                )}
                <button type="submit" className="auth-submit-btn">
                  Authorize
                </button>
              </form>
              <button
                className="auth-close-link"
                onClick={() => setIsAdminAuthOpen(false)}
              >
                Cancel
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

function App() {
  return (
    <Routes>
      <Route path="/" element={<TravelApp />} />
      <Route path="/bookpage" element={<BookingPage />} />
      <Route path="/admin" element={<AdminPage />} />
      <Route path="/explore" element={<Explore />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/stays" element={<Stays />} />
      <Route path="/journal" element={<Journal />} />
    </Routes>
  );
}

export default App;
